import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { FileText, Package, Calculator, Paperclip, Truck, Lock, CheckCircle, AlertTriangle, RefreshCw, Send, History, Clock } from 'lucide-react';
import POHeaderTab from './POHeaderTab';
import POItemsTab, { rateInclToExcl, rateExclToIncl } from './POItemsTab';
import POTaxSummaryTab from './POTaxSummaryTab';
import POAttachmentsTab from './POAttachmentsTab';
import POGrnTab from './POGrnTab';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Readable status → Tailwind class maps (replaces nested ternaries flagged in code review).
const PO_STATUS_BADGE_CLASS = {
  draft: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  received: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  resubmitted: 'bg-purple-100 text-purple-800',
  waiting_approval: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
};

const APPROVAL_ACTION_BG = {
  approved: 'bg-green-50 border-green-200',
  rejected: 'bg-red-50 border-red-200',
  resubmitted: 'bg-purple-50 border-purple-200',
  submitted: 'bg-amber-50 border-amber-200',
};

const getPoStatusBadgeClass = (status) => PO_STATUS_BADGE_CLASS[status] || 'bg-yellow-100 text-yellow-800';
const getPoStatusLabel = (status) => (status === 'waiting_approval' ? 'PENDING APPROVAL' : String(status || '').replace('_', ' ').toUpperCase());
const getApprovalActionBg = (action) => APPROVAL_ACTION_BG[action] || 'bg-gray-50 border-gray-200';

const TAB_CONFIG = [
  { id: 'header', label: 'Header Details', icon: FileText },
  { id: 'items', label: 'Product Items', icon: Package },
  { id: 'tax_summary', label: 'Tax & Summary', icon: Calculator },
  { id: 'attachments', label: 'Attachments', icon: Paperclip },
  { id: 'grn', label: 'GRN / Receiving', icon: Truck },
  { id: 'history', label: 'History', icon: History },
];

function POTabDialog({ open, onOpenChange, editingPO, suppliers, products, onSuccess, userAccess }) {
  const [activeTab, setActiveTab] = useState('header');
  const [loading, setLoading] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    supplier_id: '',
    po_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    payment_type: '',
    status: 'draft',
  });
  const [items, setItems] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [notes, setNotes] = useState('');
  
  // Reset form when dialog opens/closes or editing PO changes
  useEffect(() => {
    if (open) {
      if (editingPO) {
        setFormData({
          supplier_id: editingPO.supplier_id,
          po_date: editingPO.po_date,
          expected_delivery_date: editingPO.expected_delivery_date || '',
          payment_type: editingPO.payment_type || '',
          status: editingPO.status,
        });
        setItems(editingPO.items.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          product_sku: item.product_sku || '',
          quantity: item.quantity || 1,
          unit: item.unit || 'PCS',
          // Stored rate is EXCL-GST. Convert back to INCL-GST for the input field.
          purchase_rate: rateExclToIncl(item.purchase_rate || 0, item.gst_percentage ?? 18),
          discount_percentage: item.discount_percentage || 0,
          gst_percentage: item.gst_percentage ?? 18,
          amount_before_tax: item.amount_before_tax || 0,
          gst_amount: item.gst_amount || 0,
          line_total: item.line_total || 0,
        })));
        setAttachments(editingPO.attachments || []);
        setNotes(editingPO.notes || '');
      } else {
        resetForm();
      }
      setActiveTab('header');
    }
    // State setters + resetForm are stable across renders; only open/editingPO drive the reset
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingPO]);
  
  function resetForm() {
    setFormData({
      supplier_id: '',
      po_date: new Date().toISOString().split('T')[0],
      expected_delivery_date: '',
      payment_type: '',
      status: 'draft',
    });
    setItems([]);
    setAttachments([]);
    setNotes('');
  }
  
  function hasTabAccess(tabId) {
    if (!userAccess) return true;
    if (userAccess.is_admin) return true;
    return userAccess.tab_access[tabId] !== false;
  }
  
  function canPerformAction(action) {
    if (!userAccess) return true;
    if (userAccess.is_admin) return true;
    return userAccess.action_permissions[action] === true;
  }
  
  function validateForm() {
    const mandatory = userAccess?.mandatory_fields || [];
    
    if (!formData.supplier_id) {
      toast.error('Please select a supplier');
      setActiveTab('header');
      return false;
    }
    
    if (!formData.payment_type) {
      toast.error('Please select the Payment Type (Paid or Credit)');
      setActiveTab('header');
      return false;
    }

    if (mandatory.includes('expected_delivery_date') && !formData.expected_delivery_date) {
      toast.error('Expected delivery date is mandatory');
      setActiveTab('header');
      return false;
    }
    
    if (items.length === 0) {
      toast.error('Please add at least one item');
      setActiveTab('items');
      return false;
    }
    
    if (mandatory.includes('attachments') && attachments.length === 0) {
      toast.error('At least one attachment is mandatory');
      setActiveTab('attachments');
      return false;
    }
    
    return true;
  }
  
  // Per May 2026 rate-convention change: items[].purchase_rate in state is
  // INCL-GST (what user typed). Backend expects EXCL-GST. Convert before send.
  function itemsToBackendPayload(stateItems) {
    return stateItems.map(it => ({
      ...it,
      purchase_rate: rateInclToExcl(it.purchase_rate || 0, it.gst_percentage ?? 18),
    }));
  }
  
  async function handleSubmit() {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: 'Bearer ' + token };
      
      const payload = {
        ...formData,
        items: itemsToBackendPayload(items),
        attachments: attachments,
        notes: notes,
      };
      
      if (editingPO) {
        await axios.put(API + '/purchase-orders/' + editingPO.id, payload, { headers });
        toast.success('Purchase Order updated successfully');
      } else {
        await axios.post(API + '/purchase-orders', payload, { headers });
        toast.success('Purchase Order created successfully');
      }
      
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save Purchase Order');
    } finally {
      setLoading(false);
    }
  }
  
  // Allow editing for: draft, sent, and REJECTED POs (for resubmission)
  const canEdit = !editingPO || ['draft', 'sent', 'rejected'].includes(editingPO?.status);
  const isViewOnly = editingPO && !canEdit;
  const isRejectedPO = editingPO?.status === 'rejected';
  
  // Resubmit handler for rejected POs
  async function handleResubmit() {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: 'Bearer ' + token };
      
      // First save the updated PO
      const payload = {
        ...formData,
        items: itemsToBackendPayload(items),
        attachments: attachments,
        notes: notes,
      };
      
      await axios.put(API + '/purchase-orders/' + editingPO.id, payload, { headers });
      
      // Then resubmit for approval
      await axios.post(API + '/purchase-orders/' + editingPO.id + '/resubmit', {
        comment: 'Resubmitted after corrections'
      }, { headers });
      
      toast.success('Purchase Order updated and resubmitted for approval');
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resubmit Purchase Order');
    } finally {
      setLoading(false);
    }
  }
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-blue-600" />
            {editingPO ? `Edit PO - ${editingPO.po_number}` : 'Create Purchase Order'}
            {editingPO && (
              <Badge className={getPoStatusBadgeClass(editingPO.status)}>
                {getPoStatusLabel(editingPO.status)}
              </Badge>
            )}
            {isRejectedPO && (
              <span className="text-sm font-normal text-orange-600">(Edit & Resubmit)</span>
            )}
            {isViewOnly && (
              <span className="text-sm font-normal text-gray-500">(View Only)</span>
            )}
          </DialogTitle>
        </DialogHeader>
        
        {/* Rejection Banner for Rejected POs */}
        {isRejectedPO && editingPO?.rejection_reason && (
          <div className="mx-2 p-3 bg-red-100 border border-red-300 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-semibold text-red-800">Rejection Reason:</div>
              <div className="text-red-700">{editingPO.rejection_reason}</div>
              <div className="text-sm text-red-600 mt-1">
                Rejected by {editingPO.rejected_by_name} on {new Date(editingPO.rejected_at).toLocaleDateString()}
              </div>
            </div>
          </div>
        )}
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid grid-cols-6 gap-1 flex-shrink-0 bg-gray-100 p-1 rounded-lg">
            {TAB_CONFIG.map(tab => {
              const Icon = tab.icon;
              const hasAccess = hasTabAccess(tab.id);
              
              return (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  disabled={!hasAccess}
                  className="flex items-center gap-2 text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  data-testid={`tab-${tab.id}`}
                >
                  {hasAccess ? <Icon className="w-4 h-4" /> : <Lock className="w-4 h-4 text-gray-400" />}
                  <span className="hidden sm:inline">{tab.label}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>
          
          <div className="flex-1 overflow-y-auto py-4">
            <TabsContent value="header" className="mt-0">
              <POHeaderTab
                formData={formData}
                setFormData={setFormData}
                suppliers={suppliers}
                mandatoryFields={userAccess?.mandatory_fields || []}
                disabled={isViewOnly}
              />
            </TabsContent>
            
            <TabsContent value="items" className="mt-0">
              <POItemsTab
                items={items}
                setItems={setItems}
                products={products}
                disabled={isViewOnly}
                userRole={userAccess?.role || 'staff'}
                onBulkSupplierGroups={(groups) => {
                  // Auto-fill supplier in Header tab when the bulk upload
                  // targets a single supplier. Keeps the user from having to
                  // re-pick a supplier they already named in the CSV.
                  const keys = Object.keys(groups || {});
                  if (keys.length === 1 && !formData.supplier_id) {
                    const only = groups[keys[0]];
                    setFormData(prev => ({ ...prev, supplier_id: only?.supplier_id || keys[0] }));
                  }
                }}
              />
            </TabsContent>
            
            <TabsContent value="tax_summary" className="mt-0">
              <POTaxSummaryTab
                items={items}
                isSameState={true}
              />
            </TabsContent>
            
            <TabsContent value="attachments" className="mt-0">
              <POAttachmentsTab
                attachments={attachments}
                setAttachments={setAttachments}
                notes={notes}
                setNotes={setNotes}
                mandatoryFields={userAccess?.mandatory_fields || []}
                disabled={isViewOnly}
              />
            </TabsContent>
            
            <TabsContent value="grn" className="mt-0">
              <POGrnTab
                purchaseOrder={editingPO}
                onGrnSuccess={onSuccess}
                hasPermission={canPerformAction('receive_grn')}
              />
            </TabsContent>
            
            {/* PO History Tab */}
            <TabsContent value="history" className="mt-0">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                  <History className="w-5 h-5" />
                  PO Action History
                </h3>
                {editingPO?.approval_history && editingPO.approval_history.length > 0 ? (
                  <div className="space-y-3">
                    {[...editingPO.approval_history].reverse().map((entry, idx) => (
                      <div key={`${entry.action}-${entry.timestamp}-${idx}`} className={`p-3 rounded-lg border ${
                        entry.action === 'approved' ? 'bg-green-50 border-green-200' :
                        entry.action === 'rejected' ? 'bg-red-50 border-red-200' :
                        entry.action === 'resubmitted' ? 'bg-purple-50 border-purple-200' :
                        entry.action === 'submitted' ? 'bg-amber-50 border-amber-200' :
                        'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            {entry.action === 'approved' && <CheckCircle className="w-5 h-5 text-green-600" />}
                            {entry.action === 'rejected' && <AlertTriangle className="w-5 h-5 text-red-600" />}
                            {entry.action === 'resubmitted' && <RefreshCw className="w-5 h-5 text-purple-600" />}
                            {entry.action === 'submitted' && <Send className="w-5 h-5 text-amber-600" />}
                            {!['approved', 'rejected', 'resubmitted', 'submitted'].includes(entry.action) && <Clock className="w-5 h-5 text-gray-500" />}
                            <span className="font-semibold capitalize">{entry.action}</span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(entry.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <div className="mt-2 text-sm text-gray-600">
                          <span className="font-medium">By:</span> {entry.by_name} ({entry.by_role})
                        </div>
                        {entry.comment && (
                          <div className="mt-1 text-sm">
                            <span className="font-medium text-gray-700">Comment:</span> {entry.comment}
                          </div>
                        )}
                        {entry.previous_rejection_reason && (
                          <div className="mt-1 text-xs text-red-600">
                            Previous rejection: {entry.previous_rejection_reason}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <History className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No action history yet</p>
                    <p className="text-sm">History will appear after the PO is submitted for approval</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </div>
        </Tabs>
        
        {/* Footer Actions */}
        <div className="flex-shrink-0 flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-gray-500">
            {userAccess && !userAccess.is_admin && (
              <span className="flex items-center gap-1">
                <Lock className="w-3 h-3" />
                Limited access based on your role
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="po-cancel-btn"
            >
              {isViewOnly ? 'Close' : 'Cancel'}
            </Button>
            {/* Regular save button for draft/sent */}
            {!isViewOnly && !isRejectedPO && canPerformAction(editingPO ? 'edit' : 'create') && (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                data-testid="po-submit-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {loading ? 'Saving...' : editingPO ? 'Update PO' : 'Create PO'}
              </Button>
            )}
            {/* Save Changes button for rejected PO */}
            {isRejectedPO && (
              <>
                <Button
                  variant="outline"
                  onClick={handleSubmit}
                  disabled={loading}
                  data-testid="po-save-changes-btn"
                >
                  Save Changes
                </Button>
                <Button
                  onClick={handleResubmit}
                  disabled={loading}
                  className="bg-orange-500 hover:bg-orange-600"
                  data-testid="po-resubmit-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {loading ? 'Resubmitting...' : 'Resubmit for Approval'}
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default POTabDialog;
